import { useEffect, useRef, useState } from "react";

import { t } from "../../i18n";
import { finishGoogleConnect, takeState } from "../../lib/gmail";

/**
 * Where Google sends a person back to after they consent.
 *
 * `/oauth/google`, and the path is fixed rather than chosen at runtime because
 * it has to match what is registered in the Google Cloud console exactly —
 * Google refuses anything else, which is the point of registering it.
 *
 * The page's whole job is to hand the one-time code to the Edge Function and
 * get out of the way. Two things it is careful about:
 *
 *   - **The code is exchanged once.** React runs an effect twice in
 *     development's strict mode, and a code that has already been spent comes
 *     back from Google as `invalid_grant` — which would show a person a failure
 *     for a connection that had, in fact, just succeeded.
 *   - **The URL is cleaned up straight away.** An authorization code in the
 *     address bar ends up in history and in anything the browser syncs. It is
 *     spent by then, but there is no reason to leave it lying about.
 */
export function GoogleReturn() {
  const [state, setState] = useState<
    | { kind: "working" }
    | { kind: "done"; email: string }
    | { kind: "failed"; message: string }
  >({ kind: "working" });
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const returnedState = params.get("state");
    const refusal = params.get("error");
    const expected = takeState();

    window.history.replaceState(null, "", window.location.pathname);

    async function finish() {
      if (refusal) {
        setState({
          kind: "failed",
          message:
            refusal === "access_denied"
              ? t("You cancelled at Google, so nothing was connected.")
              : t("Google returned an error: {error}", { error: refusal }),
        });
        return;
      }
      if (!code) {
        setState({ kind: "failed", message: t("Google sent no code back.") });
        return;
      }
      // The handshake has to be the one this tab started. A code arriving from
      // anywhere else is not a connection somebody asked for.
      if (!expected || expected !== returnedState) {
        setState({
          kind: "failed",
          message: t(
            "This connection did not start in this tab. Open Tools → Integrations and press Connect Gmail again.",
          ),
        });
        return;
      }

      try {
        setState({ kind: "done", email: await finishGoogleConnect(code) });
      } catch (e) {
        setState({
          kind: "failed",
          message: e instanceof Error ? e.message : String(e),
        });
      }
    }

    void finish();
  }, []);

  return (
    <div className="wrap" style={{ maxWidth: 520, marginTop: 60 }}>
      {state.kind === "working" && (
        <div className="stub">{t("Finishing the connection with Google…")}</div>
      )}

      {state.kind === "done" && (
        <div className="shelf">
          <b>{t("Gmail connected")}</b>
          {t("Emails you send from a job will come from {email}.", {
            email: state.email,
          })}
        </div>
      )}

      {state.kind === "failed" && (
        <div className="err" role="alert">
          {state.message}
        </div>
      )}

      {state.kind !== "working" && (
        <button
          className="addrow"
          style={{ marginTop: 12 }}
          onClick={() => window.location.assign("/")}
        >
          {t("Back to the app")}
        </button>
      )}
    </div>
  );
}
